import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, Upload as UploadIcon, CheckCircle, Trash2, Check } from 'lucide-react';
import { bulkUploadService } from '../../services/memberships/bulk-upload.service';
import type { BulkUploadResult } from '../../types';
import { NotificationToast } from '../ui/NotificationToast';

interface BulkUploadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback para refrescar la lista de membresías
  uploadType?: 'basicos' | 'complementarios';
}

export const BulkUploadDrawer: React.FC<BulkUploadDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  uploadType = 'basicos'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      // Reset state when closing
      setTimeout(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setIsUploading(false);
        setUploadResult(null);
        setError(null);
      }, 300);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    // Llamar onClose después de un pequeño delay para permitir la animación
    setTimeout(() => {
      onClose();
    }, 300); // Mismo tiempo que la duración de la transición CSS
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const excelFile = files.find(file => 
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls')
    );

    if (excelFile) {
      try {
        bulkUploadService.validateFile(excelFile);
        setSelectedFile(excelFile);
        setError(null);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Error al validar el archivo');
      }
    } else {
      setError('Por favor seleccione un archivo Excel válido (.xlsx o .xls)');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (file) {
      try {
        bulkUploadService.validateFile(file);
        setSelectedFile(file);
        setError(null);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Error al validar el archivo');
      }
    } else {
    }
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    
    if (!selectedFile) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Paso 1: Procesar el archivo Excel (30%)
      setUploadProgress(10);
      const data = await bulkUploadService.processExcelFile(selectedFile, uploadType);
      setUploadProgress(30);

      // Capturar info de filas omitidas (nombres en vez de números de documento)
      const skippedInfo = (data as any).skippedInfo as { count: number; names: string[] } | undefined;

      // Paso 2: Enviar al backend (70%)
      setUploadProgress(50);
      let result: BulkUploadResult;
      if (uploadType === 'basicos') {
        result = await bulkUploadService.uploadMemberships(data as any);
      } else {
        result = await bulkUploadService.uploadDatosComplementarios(data as any, skippedInfo);
      }
      
      // Agregar info de omitidos al resultado para mostrar en UI
      if (skippedInfo && skippedInfo.count > 0) {
        result = {
          ...result,
          skippedCount: skippedInfo.count,
          skippedNames: skippedInfo.names
        } as any;
      }
      
      setUploadProgress(100);

      // Mostrar resultados
      setUploadResult(result);
      setIsUploading(false);
      
      // Construir mensaje sobre filas omitidas (nombres en vez de números de documento)
      let skippedMessage = '';
      if (result.skippedCount && result.skippedCount > 0) {
        skippedMessage = ` Se omitieron ${result.skippedCount} filas que no tenían número de documento válido (tenían nombres u otros datos).`;
      }

      // Mostrar toast según el resultado
      if (result.success > 0 && result.errors.length === 0) {
        // Éxito total
        setNotificationConfig({
          title: '¡Carga completada!',
          message: `Se procesaron ${result.success} registros exitosamente.${skippedMessage}`,
          type: skippedMessage ? 'warning' : 'success'
        });
        setShowNotification(true);
      } else if (result.success > 0 && result.errors.length > 0) {
        // Éxito parcial
        setNotificationConfig({
          title: 'Carga completada con advertencias',
          message: `Se procesaron ${result.success} registros exitosamente y ${result.errors.length} tuvieron errores.${skippedMessage} Revise los detalles abajo.`,
          type: 'warning'
        });
        setShowNotification(true);
      } else {
        // Todos fallaron - mostrar mensaje informativo
        const errorSummary = result.errors.slice(0, 3).map(e => {
          const doc = e.data?.numeroDocumento || e.data?.nombres || 'N/A';
          return `${doc}: ${e.message}`;
        }).join('; ');
        
        setNotificationConfig({
          title: 'No se pudo completar la carga',
          message: `Todos los ${result.total} registros fallaron.${skippedMessage} Posibles causas: los números de documento no existen en la base de datos. Ejemplos: ${errorSummary}${result.errors.length > 3 ? '...' : ''}`,
          type: 'error'
        });
        setShowNotification(true);
      }

      // Si fue exitoso y hay callback, ejecutarlo
      if (result.success > 0 && onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setIsUploading(false);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido durante la carga';
      setError(errorMessage);
      setUploadProgress(0);
      
      // Mostrar toast de error
      setNotificationConfig({
        title: 'Error en la carga masiva',
        message: errorMessage,
        type: 'error'
      });
      setShowNotification(true);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-medium text-gray-900">
                  {uploadType === 'basicos' ? 'Carga Masiva de Datos Básicos' : 'Carga Masiva de Datos Complementarios'}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Errores de validación */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
                    <p className="text-red-800 text-xs">{error}</p>
                  </div>
                </div>
              )}

              {!uploadResult ? (
                <>
                  {/* Instrucciones */}
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-900 mb-3">
                      Instrucciones para la carga masiva
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      {uploadType === 'basicos' ? (
                        <ul className="text-xs text-blue-800 space-y-1">
                          <li>• Completa todos los campos obligatorios</li>
                          <li>• Asegúrate de que los emails sean únicos y válidos</li>
                          <li>• Las fechas deben estar en formato DD/MM/YYYY</li>
                          <li>• Máximo 1000 registros por archivo</li>
                        </ul>
                      ) : (
                        <ul className="text-xs text-blue-800 space-y-1">
                          <li>• Incluye el número de documento para vincular con membresías existentes</li>
                          <li>• Si un miembro no existe, no se procesará ese registro</li>
                          <li>• Los campos de estudios pueden estar vacíos si no aplican</li>
                          <li>• Máximo 1000 registros por archivo</li>
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Zona de carga */}
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-900 mb-3">
                      Cargar archivo Excel
                    </h3>
                    
                    {!selectedFile ? (
                      <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                          isDragActive 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          {isDragActive ? 'Suelta el archivo aquí' : 'Arrastra tu archivo Excel aquí'}
                        </p>
                        <p className="text-gray-500 mb-4">o</p>
                        <button
                          onClick={handleBrowseFiles}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          Seleccionar archivo
                        </button>
                        <p className="text-xs text-gray-500 mt-3">
                          Solo archivos .xlsx y .xls
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                            <div>
                              <p className="font-medium text-gray-900">{selectedFile.name}</p>
                              <p className="text-xs text-gray-500">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={handleRemoveFile}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>

                  {/* Barra de progreso */}
                  {isUploading && (
                    <div className="mb-6">
                      <div className="flex justify-between text-xs text-gray-600 mb-2">
                        <span>Procesando archivo...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Resultados de la carga */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xs font-medium text-gray-900 mb-2">
                      Carga completada
                    </h3>
                  </div>

                  {/* Resumen */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Resumen del procesamiento</h4>
                    <div className={`grid gap-4 text-center ${uploadResult.skippedCount ? 'grid-cols-4' : 'grid-cols-3'}`}>
                      <div>
                        <div className="text-xs font-medium text-green-600">{uploadResult.success}</div>
                        <div className="text-xs text-gray-500">Exitosos</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-red-600">{uploadResult.errors?.length || 0}</div>
                        <div className="text-xs text-gray-500">Errores</div>
                      </div>
                      {uploadResult.skippedCount ? (
                        <div>
                          <div className="text-xs font-medium text-yellow-600">{uploadResult.skippedCount}</div>
                          <div className="text-xs text-gray-500">Sin doc.</div>
                        </div>
                      ) : null}
                      <div>
                        <div className="text-xs font-medium text-gray-600">{uploadResult.total}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                    </div>
                    {uploadResult.skippedCount ? (
                      <p className="text-xs text-yellow-700 mt-3 bg-yellow-50 p-2 rounded">
                        <strong>Nota:</strong> Se omitieron {uploadResult.skippedCount} filas que no tenían número de documento válido 
                        (contenían nombres u otros textos en lugar de números).
                      </p>
                    ) : null}
                  </div>

                  {/* Errores */}
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Errores encontrados ({uploadResult.errors.length})</h4>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <ul className="text-xs text-red-800 space-y-2">
                          {uploadResult.errors.map((error, index) => {
                            const doc = error.data?.numeroDocumento || error.data?.nombres || 'N/A';
                            return (
                              <li key={index} className="border-b border-red-100 pb-1 last:border-0">
                                <span className="font-medium">{doc}:</span> {error.message}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        <strong>Nota:</strong> Para cargar datos complementarios, primero deben existir las membresías con los números de documento correspondientes en el sistema.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              {!uploadResult ? (
                <div className="flex space-x-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isUploading ? 'Procesando...' : 'Procesar archivo'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleClose}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  Finalizar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={8000}
        />
      )}
    </div>
  );
};

export default BulkUploadDrawer;